/**
 * useParsingTimeout — Phase 2.7 (Cancel upload + Timeout polling, 2026-08-08).
 *
 * Возвращает true, если dataset пробыл в (uploaded|parsing) дольше
 * PARSING_TIMEOUT_MS. Использует createdAt из БД (не dataUpdatedAt из query),
 * потому что:
 *  - dataUpdatedAt в TanStack = время последнего ответа query. Query идёт
 *    каждые 2-3 секунды, и при каждом успешном ответе dataUpdatedAt
 *    обновляется — это бы зациклило таймер.
 *  - createdAt — стабильная точка отсчёта: момент, когда сервер создал
 *    dataset. Polling 60s timeout заведомо больше server-side processing.
 *
 * Используется в FilesResultsPage для показа кнопок Retry / Refresh
 * в строке, если статус застрял.
 *
 * Поведение:
 *  - status in (uploaded, parsing) + elapsed >= timeout → true.
 *  - status === 'ready' / 'failed' → false (терминал).
 *  - dataset === null → false.
 *  - F5: useEffect пересоздаёт setTimeout от текущего elapsed; через
 *    timeout tick переключает в true. После Refresh (qc.invalidateQueries)
 *    useDataset вернёт свежий статус, useEffect пересчитает.
 */
import { useEffect, useState } from 'react';

import { PARSING_TIMEOUT_MS } from '@/lib/constants';
import type { Dataset, DatasetStatus } from '@/hooks/useFiles';

const WATCH_STATUSES: ReadonlySet<DatasetStatus> = new Set<DatasetStatus>([
  'uploaded',
  'parsing',
]);

export function useParsingTimeout(dataset: Dataset | null | undefined): boolean {
  const [isTimeout, setIsTimeout] = useState(false);

  useEffect(() => {
    // Сбрасываем при смене dataset'а — особенно важно при удалении.
    setIsTimeout(false);
    if (!dataset) return;
    if (!WATCH_STATUSES.has(dataset.status)) return;

    const startTs = new Date(dataset.created_at).getTime();
    if (!Number.isFinite(startTs)) return;

    const computeAndSchedule = () => {
      const elapsed = Date.now() - startTs;
      if (elapsed >= PARSING_TIMEOUT_MS) {
        setIsTimeout(true);
        return null; // уже timeout — следующий setTimeout не нужен
      }
      return window.setTimeout(() => {
        // Recheck при срабатывании: dataset мог уже стать ready/failed
        // (через props useEffect перезапустит, но защитимся).
        const cur = Date.now() - startTs;
        if (cur >= PARSING_TIMEOUT_MS) setIsTimeout(true);
      }, PARSING_TIMEOUT_MS - elapsed);
    };

    const id = computeAndSchedule();
    return () => {
      if (id !== null) window.clearTimeout(id);
    };
  }, [dataset?.id, dataset?.status, dataset?.created_at, dataset]);

  return isTimeout;
}
